import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Table, Form, InputGroup, Button, Pagination, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DataManager } from '../../../core/data-manager';
import { ResourceManager } from '../../../core/resource-manager';
import { entity_language } from '../../../config/entity_language';
import JSZip from 'jszip';
import ResourceDetail from './components/ResourceDetail';
import './style.css';

function getEntityLabel(entityName) {
    if (!entityName) return '-';
    return entity_language.entity[entityName] || entityName;
}

function getAllKnownEntities(availableFromDb) {
    const allEntities = new Set(availableFromDb);
    Object.keys(entity_language.entity).forEach(e => allEntities.add(e));
    return [...allEntities].sort();
}

function base64ToBlob(raw) {
    var byteString, mime;
    if (raw.indexOf(',') !== -1) {
        var parts = raw.split(',');
        var mimeMatch = parts[0].match(/:(.*?);/);
        mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
        byteString = atob(parts[1]);
    } else {
        mime = 'application/octet-stream';
        byteString = atob(raw);
    }
    var ab = new ArrayBuffer(byteString.length);
    var ia = new Uint8Array(ab);
    for (var i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mime });
}

async function downloadResourceAsBlob(resource) {
    const response = await ResourceManager.getFile(resource.hash, resource.entity);
    if (response.success === 1 && response.base64) {
        return base64ToBlob(response.base64);
    }
    return null;
}

async function triggerDownload(resource) {
    const blob = await downloadResourceAsBlob(resource);
    if (!blob) return;
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = resource.filename || (resource.hash + '.' + resource.extension);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
}

const ITEMS_PER_PAGE = 25;

const EXTENSION_ICONS = {
    'pdf': { icon: 'file-pdf', color: '#dc3545' },
    'doc': { icon: 'file-word', color: '#2b579a' },
    'docx': { icon: 'file-word', color: '#2b579a' },
    'xls': { icon: 'file-excel', color: '#217346' },
    'xlsx': { icon: 'file-excel', color: '#217346' },
    'csv': { icon: 'file-csv', color: '#217346' },
    'ppt': { icon: 'file-powerpoint', color: '#d24726' },
    'pptx': { icon: 'file-powerpoint', color: '#d24726' },
    'zip': { icon: 'file-zipper', color: '#f0ad4e' },
    'rar': { icon: 'file-zipper', color: '#f0ad4e' },
    '7z': { icon: 'file-zipper', color: '#f0ad4e' },
    'txt': { icon: 'file-lines', color: '#6c757d' },
    'json': { icon: 'file-code', color: '#6c757d' },
    'xml': { icon: 'file-code', color: '#6c757d' },
    'html': { icon: 'file-code', color: '#e44d26' },
    'css': { icon: 'file-code', color: '#264de4' },
    'js': { icon: 'file-code', color: '#f7df1e' },
    'mp4': { icon: 'file-video', color: '#6f42c1' },
    'avi': { icon: 'file-video', color: '#6f42c1' },
    'mov': { icon: 'file-video', color: '#6f42c1' },
    'mp3': { icon: 'file-audio', color: '#fd7e14' },
    'wav': { icon: 'file-audio', color: '#fd7e14' },
};

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'];

function getFileIcon(extension) {
    const ext = (extension || '').toLowerCase();
    if (IMAGE_EXTENSIONS.includes(ext)) {
        return { icon: 'file-image', color: '#0d6efd' };
    }
    return EXTENSION_ICONS[ext] || { icon: 'file', color: '#6c757d' };
}

function isImage(extension) {
    return IMAGE_EXTENSIONS.includes((extension || '').toLowerCase());
}

function formatDate(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('it-IT', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
}

function formatFileSize(extension) {
    const ext = (extension || '').toUpperCase();
    return ext || '-';
}

export default function Resources() {
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [entityFilter, setEntityFilter] = useState('');
    const [extensionFilter, setExtensionFilter] = useState('');
    const [availableEntities, setAvailableEntities] = useState([]);
    const [availableExtensions, setAvailableExtensions] = useState([]);
    const [sorting, setSorting] = useState({ field: 'id', order: 'DESC' });
    const [selectedResource, setSelectedResource] = useState(null);
    const [showDetail, setShowDetail] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [downloading, setDownloading] = useState(false);
    const allEntities = getAllKnownEntities(availableEntities);

    const loadResources = useCallback(async () => {
        setLoading(true);
        try {
            const constraints = [];
            if (entityFilter) {
                constraints.push({ fieldName: 'entity', type: 'value', value: entityFilter });
            }
            if (extensionFilter) {
                constraints.push({ fieldName: 'extension', type: 'value', value: extensionFilter });
            }

            let response;
            if (searchQuery || constraints.length > 0) {
                response = await DataManager.searchItems(
                    'resource', page, ITEMS_PER_PAGE, sorting,
                    searchQuery, ['filename', 'entity', 'hash'], constraints
                );
            } else {
                response = await DataManager.getItems('resource', page, ITEMS_PER_PAGE, sorting);
            }

            if (response.success === 1) {
                setResources(response.body || []);
                const total = response.total || response.body?.length || 0;
                setTotalItems(total);
                setTotalPages(Math.ceil(total / ITEMS_PER_PAGE) || 1);
            }
        } catch (error) {
            console.error('Error loading resources:', error);
        } finally {
            setLoading(false);
        }
    }, [page, sorting, searchQuery, entityFilter, extensionFilter]);

    const loadFilters = useCallback(async () => {
        try {
            const allRes = await DataManager.getItems('resource', 1, 9999, { field: 'entity', order: 'ASC' });
            if (allRes.success === 1 && allRes.body) {
                const entities = [...new Set(allRes.body.map(r => r.entity).filter(Boolean))].sort();
                const extensions = [...new Set(allRes.body.map(r => r.extension).filter(Boolean))].sort();
                setAvailableEntities(entities);
                setAvailableExtensions(extensions);
            }
        } catch (error) {
            console.error('Error loading filters:', error);
        }
    }, []);

    useEffect(() => {
        loadResources();
    }, [loadResources]);

    useEffect(() => {
        loadFilters();
    }, [loadFilters]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
    };

    const handleSearchChange = (value) => {
        setSearchQuery(value);
        setPage(1);
    };

    const handleSort = (field) => {
        setSorting(prev => ({
            field,
            order: prev.field === field && prev.order === 'ASC' ? 'DESC' : 'ASC'
        }));
        setPage(1);
    };

    const handleRowClick = (resource) => {
        setSelectedResource(resource);
        setShowDetail(true);
    };

    const handleCloseDetail = () => {
        setShowDetail(false);
        setSelectedResource(null);
    };

    const handleResetFilters = () => {
        setSearchQuery('');
        setEntityFilter('');
        setExtensionFilter('');
        setPage(1);
    };

    const handleSelectToggle = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleSelectAll = () => {
        if (selectedIds.size === resources.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(resources.map(r => r.id)));
        }
    };

    const handleDownloadSelected = async () => {
        const selected = resources.filter(r => selectedIds.has(r.id));
        if (selected.length === 0) return;

        if (selected.length === 1) {
            await triggerDownload(selected[0]);
            return;
        }

        setDownloading(true);
        try {
            const zip = new JSZip();
            const filenameCount = {};
            for (const res of selected) {
                const blob = await downloadResourceAsBlob(res);
                if (!blob) continue;
                let name = res.filename || (res.hash + '.' + res.extension);
                if (filenameCount[name]) {
                    filenameCount[name]++;
                    const parts = name.split('.');
                    const ext = parts.length > 1 ? '.' + parts.pop() : '';
                    name = parts.join('.') + '_' + filenameCount[name] + ext;
                } else {
                    filenameCount[name] = 1;
                }
                zip.file(name, blob);
            }
            const content = await zip.generateAsync({ type: 'blob' });
            const blobUrl = URL.createObjectURL(content);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = 'risorse_' + new Date().toISOString().slice(0, 10) + '.zip';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Error creating ZIP:', error);
        } finally {
            setDownloading(false);
        }
    };

    const getFileUrl = (resource) => {
        return ResourceManager.getFileUrl(
            { hash: resource.hash, extension: resource.extension },
            resource.entity
        );
    };

    const renderSortIcon = (field) => {
        if (sorting.field !== field) return <FontAwesomeIcon icon="sort" className="ms-1 text-muted" size="xs" />;
        return <FontAwesomeIcon icon={sorting.order === 'ASC' ? 'sort-up' : 'sort-down'} className="ms-1" size="xs" />;
    };

    const renderThumbnail = (resource) => {
        if (isImage(resource.extension)) {
            return (
                <img
                    src={getFileUrl(resource)}
                    alt={resource.filename}
                    className="resources-page-thumbnail"
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
            );
        }
        const { icon, color } = getFileIcon(resource.extension);
        return <FontAwesomeIcon icon={icon} style={{ color }} size="lg" />;
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;
        const items = [];
        const maxVisible = 5;
        let start = Math.max(1, page - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible - 1);
        if (end - start < maxVisible - 1) {
            start = Math.max(1, end - maxVisible + 1);
        }

        items.push(
            <Pagination.First key="first" onClick={() => setPage(1)} disabled={page === 1} />,
            <Pagination.Prev key="prev" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} />
        );
        if (start > 1) items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
        for (let i = start; i <= end; i++) {
            items.push(
                <Pagination.Item key={i} active={i === page} onClick={() => setPage(i)}>{i}</Pagination.Item>
            );
        }
        if (end < totalPages) items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
        items.push(
            <Pagination.Next key="next" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} />,
            <Pagination.Last key="last" onClick={() => setPage(totalPages)} disabled={page === totalPages} />
        );
        return <Pagination className="mb-0 justify-content-center">{items}</Pagination>;
    };

    const hasFilters = searchQuery || entityFilter || extensionFilter;

    return (
        <Container fluid className="resources-page py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h4 className="mb-0">
                        <FontAwesomeIcon icon="folder-open" className="me-2 text-primary" />
                        Risorse di sistema
                    </h4>
                    <small className="text-muted">{totalItems} file totali</small>
                </Col>
            </Row>

            <Card className="resources-page-filters mb-3">
                <Card.Body className="py-2">
                    <Form onSubmit={handleSearch}>
                        <Row className="g-2 align-items-end">
                            <Col md={4} lg={4}>
                                <InputGroup size="sm">
                                    <InputGroup.Text>
                                        <FontAwesomeIcon icon="search" />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        placeholder="Cerca per nome file..."
                                        value={searchQuery}
                                        onChange={(e) => handleSearchChange(e.target.value)}
                                    />
                                </InputGroup>
                            </Col>
                            <Col md={3} lg={3}>
                                <Form.Select
                                    size="sm"
                                    value={entityFilter}
                                    onChange={(e) => { setEntityFilter(e.target.value); setPage(1); }}
                                >
                                    <option value="">Tutte le entit&agrave;</option>
                                    {allEntities.map(e => (
                                        <option key={e} value={e}>{getEntityLabel(e)} ({e})</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={3} lg={3}>
                                <Form.Select
                                    size="sm"
                                    value={extensionFilter}
                                    onChange={(e) => { setExtensionFilter(e.target.value); setPage(1); }}
                                >
                                    <option value="">Tutte le estensioni</option>
                                    {availableExtensions.map(ext => (
                                        <option key={ext} value={ext}>.{ext}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={2} lg={2}>
                                {hasFilters && (
                                    <Button variant="outline-secondary" size="sm" onClick={handleResetFilters} className="w-100">
                                        <FontAwesomeIcon icon="times" className="me-1" /> Reset
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {selectedIds.size > 0 && (
                <Card className="resources-page-selection-bar mb-3">
                    <Card.Body className="py-2 d-flex align-items-center justify-content-between">
                        <span>
                            <FontAwesomeIcon icon="check-square" className="me-2 text-primary" />
                            <strong>{selectedIds.size}</strong> file selezionati
                        </span>
                        <div className="d-flex gap-2">
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleDownloadSelected}
                                disabled={downloading}
                            >
                                {downloading ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-1" />
                                        Creazione ZIP...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon="download" className="me-1" />
                                        {selectedIds.size === 1 ? 'Scarica file' : 'Scarica ZIP'}
                                    </>
                                )}
                            </Button>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setSelectedIds(new Set())}
                            >
                                <FontAwesomeIcon icon="times" className="me-1" />
                                Deseleziona
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            )}

            <Card className="resources-page-table-card">
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="resources-page-loading">
                            <Spinner animation="border" variant="primary" />
                            <span className="ms-2">Caricamento...</span>
                        </div>
                    ) : resources.length === 0 ? (
                        <div className="resources-page-empty">
                            <FontAwesomeIcon icon="folder-open" size="3x" className="text-muted mb-3" />
                            <p className="text-muted mb-0">
                                {hasFilters ? 'Nessun risultato trovato' : 'Nessuna risorsa presente'}
                            </p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover className="resources-page-table mb-0">
                                <thead>
                                    <tr>
                                        <th style={{ width: '40px' }} className="text-center">
                                            <Form.Check
                                                type="checkbox"
                                                checked={resources.length > 0 && selectedIds.size === resources.length}
                                                onChange={handleSelectAll}
                                            />
                                        </th>
                                        <th style={{ width: '50px' }}></th>
                                        <th className="sortable" onClick={() => handleSort('filename')}>
                                            Nome file {renderSortIcon('filename')}
                                        </th>
                                        <th className="sortable" onClick={() => handleSort('entity')} style={{ width: '160px' }}>
                                            Entit&agrave; {renderSortIcon('entity')}
                                        </th>
                                        <th style={{ width: '100px' }}>Tipo</th>
                                        <th className="sortable" onClick={() => handleSort('date')} style={{ width: '170px' }}>
                                            Data caricamento {renderSortIcon('date')}
                                        </th>
                                        <th style={{ width: '90px' }} className="text-center">Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {resources.map((resource) => (
                                        <tr key={resource.id} onClick={() => handleRowClick(resource)} className={`resources-page-row ${selectedIds.has(resource.id) ? 'resources-page-row-selected' : ''}`}>
                                            <td className="text-center align-middle" onClick={(e) => e.stopPropagation()}>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={selectedIds.has(resource.id)}
                                                    onChange={() => handleSelectToggle(resource.id)}
                                                />
                                            </td>
                                            <td className="text-center align-middle resources-page-icon-cell">
                                                {renderThumbnail(resource)}
                                                <span className="resources-page-fallback-icon" style={{ display: 'none' }}>
                                                    <FontAwesomeIcon icon={getFileIcon(resource.extension).icon} style={{ color: getFileIcon(resource.extension).color }} size="lg" />
                                                </span>
                                            </td>
                                            <td className="align-middle">
                                                <div className="resources-page-filename">{resource.filename}</div>
                                                <small className="text-muted">{resource.hash}</small>
                                            </td>
                                            <td className="align-middle">
                                                <Badge bg="light" text="dark" className="resources-page-entity-badge">
                                                    {getEntityLabel(resource.entity)}
                                                </Badge>
                                            </td>
                                            <td className="align-middle">
                                                <span className="resources-page-extension">.{resource.extension}</span>
                                            </td>
                                            <td className="align-middle">
                                                <small>{formatDate(resource.date)}</small>
                                            </td>
                                            <td className="align-middle text-center" style={{ whiteSpace: 'nowrap' }}>
                                                <div className="d-inline-flex gap-1">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        title="Visualizza"
                                                        onClick={(e) => { e.stopPropagation(); handleRowClick(resource); }}
                                                    >
                                                        <FontAwesomeIcon icon="eye" />
                                                    </Button>
                                                    <Button
                                                        variant="outline-success"
                                                        size="sm"
                                                        title="Scarica file"
                                                        onClick={(e) => { e.stopPropagation(); triggerDownload(resource); }}
                                                    >
                                                        <FontAwesomeIcon icon="download" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
                {totalPages > 1 && (
                    <Card.Footer className="resources-page-pagination">
                        <Row className="align-items-center">
                            <Col xs="auto">
                                <small className="text-muted">
                                    Pagina {page} di {totalPages} &mdash; {totalItems} risultati
                                </small>
                            </Col>
                            <Col className="d-flex justify-content-end">
                                {renderPagination()}
                            </Col>
                        </Row>
                    </Card.Footer>
                )}
            </Card>

            {showDetail && selectedResource && (
                <ResourceDetail
                    resource={selectedResource}
                    onClose={handleCloseDetail}
                    getFileUrl={getFileUrl}
                />
            )}
        </Container>
    );
}
